import { Subject } from 'rxjs';

export type KeyEventType = {
  code: string;
  event: 'up' | 'down';
};

export type KeyPressedEventType = {
  keys: KeyInputState;
};

export class InputHandler {
  public keyState: KeyInputState;
  public mouseState: MouseInputState;

  public keyEvents: Subject<KeyEventType>;
  public keyPressedEvents: Subject<KeyPressedEventType>;

  private canvasHandle: HTMLCanvasElement;

  private mouseMovedEvent?: InputMouseEvent;
  private mouseAdapters: MouseEventAdapter[];

  private registeredKeys: Set<string>;
  private keyCombos: KeyCombo[];

  private inputActions: ActionList;

  constructor(public canvas: HTMLCanvasElement) {
    this.canvasHandle = canvas;
    this.keyState = {};
    this.mouseState = {};
    this.mouseState[MouseButtonEnum.Left] = false;
    this.mouseState[MouseButtonEnum.Middle] = false;
    this.mouseState[MouseButtonEnum.Right] = false;

    this.mouseMovedEvent = undefined;
    this.mouseAdapters = [];

    this.keyCombos = [];
    this.registeredKeys = new Set();
    this.keyEvents = new Subject<KeyEventType>();
    this.keyPressedEvents = new Subject<KeyPressedEventType>();
    this.inputActions = {};
  }

  public setInputActions(inputActions: ActionList) {
    this.inputActions = inputActions;
  }

  private clearInputState() {
    this.mouseState = {};
    this.keyState = {};
  }

  public hookEvents() {
    this.canvasHandle.addEventListener('keydown', (e) => {
      this.keyDown(e as KeyboardEvent);
    });
    this.canvasHandle.addEventListener('keyup', (e) => {
      this.keyUp(e as KeyboardEvent);
    });
    this.canvasHandle.addEventListener('mousedown', (e) => {
      this.mouseDown(this.convertMouseEvent(e as MouseEvent));
    });
    this.canvasHandle.addEventListener('mouseup', (e) => {
      this.mouseUp(this.convertMouseEvent(e as MouseEvent));
    });
    this.canvasHandle.addEventListener('mousemove', (e) => {
      // Force focus of the canvas when user moves mouse over it.
      (e.currentTarget as HTMLCanvasElement).focus();
      this.mouseMove(this.convertMouseEvent(e as MouseEvent));
    });
    this.canvasHandle.addEventListener('mouseout', (e) => {
      // When mouse leaves the canvas clear the input state and blur.
      (e.currentTarget as HTMLCanvasElement).blur();
      this.clearInputState();
    });
  }

  public bindKeyMapping(keymap: KeyMap) {
    for (let actionName in keymap) {
      this.bindKeyCombo(keymap[actionName], () => {
        const action = this.inputActions[actionName];
        if (action) action();
      });
    }
  }

  public bindKeyCombo(keySet: KeySet, callback: (combo: KeyCombo, handler: InputHandler) => void) {
    if (keySet.length > 0) {
      for (let keys of keySet) {
        this.keyCombos.push({
          keys: keys,
          callback: callback,
        });

        for (let key of keys) {
          this.registeredKeys.add(key);
        }
      }
    } else {
      throw new Error('Key combination must include atleast one key');
    }
  }

  public handleKeyboard() {
    let set = new Set();

    for (let combo of this.keyCombos) {
      let run = true;

      for (let key of combo.keys) {
        if (!this.keyState[key]) {
          run = false;
          break;
        }
      }

      if (run) {
        if (combo.callback && !set.has(combo.callback)) {
          combo.callback(combo, this);
          set.add(combo.callback);
        } else {
          console.error('Triggered a key combination but no action is associated with it', combo);
        }
      }
    }
    for (let i in this.keyState) {
      if (this.keyState[i]) {
        this.keyPressedEvents.next({ keys: this.keyState });
        break;
      }
    }
  }

  public keyUp(e: KeyboardEvent) {
    this.keyState[e.code] = false;

    this.keyEvents.next({ event: 'up', code: e.code });
  }

  public keyDown(e: KeyboardEvent) {
    this.keyState[e.code] = true;
    if (this.registeredKeys.has(e.code)) {
      e.preventDefault();
    }

    this.keyEvents.next({ event: 'down', code: e.code });
  }

  private mouseDown(e: InputMouseEvent) {
    if (e.button) {
      this.mouseState[e.button] = true;

      for (let adapter of this.mouseAdapters) {
        if (adapter.mouseDownEvent) {
          adapter.mouseDownEvent(e);
        }
      }
    }
  }

  private mouseUp(e: InputMouseEvent) {
    if (e.button) {
      for (let adapter of this.mouseAdapters) {
        if (adapter.mouseUpEvent) {
          adapter.mouseUpEvent(e);
        }
      }

      this.mouseState[e.button] = false;
    }
  }

  private mouseMove(e: InputMouseEvent) {
    this.mouseMovedEvent = e; // Store move event for next render pass
  }

  public handleMouse() {
    if (this.mouseMovedEvent) {
      for (let adapter of this.mouseAdapters) {
        if (adapter.mouseMoveEvent) {
          adapter.mouseMoveEvent(this.mouseMovedEvent);
        }
      }

      this.mouseMovedEvent = undefined;
    }
  }

  public bindMouseEventAdapter(adapter: MouseEventAdapter) {
    this.mouseAdapters.push(adapter);
  }

  private convertMouseEvent(e: MouseEvent): InputMouseEvent {
    const rect = this.canvasHandle.getBoundingClientRect();

    let button: MouseButtonEnum;
    switch (e.button) {
      default:
      case 0:
        button = MouseButtonEnum.Left;
        break;
      case 1:
        button = MouseButtonEnum.Middle;
        break;
      case 2:
        button = MouseButtonEnum.Right;
        break;
    }
    const cssX = e.clientX - rect.left;
    const cssY = e.clientY - rect.top;
    // Scale from CSS pixels to canvas (render) pixels for picking/readPixels
    const scaleX = this.canvasHandle.width / rect.width;
    const scaleY = this.canvasHandle.height / rect.height;
    let relX = cssX * scaleX;
    let relY = this.canvasHandle.height - cssY * scaleY;

    let anyPressed = () => {
      for (let i in this.mouseState) {
        if (this.mouseState[i]) {
          return true;
        }
      }
      return false;
    };

    return {
      position: { x: relX, y: relY },
      button: button,
      state: this.mouseState,
      anyPressed: anyPressed,
    };
  }
}

type KeyMap = {
  [key: string]: KeySet;
};

type ActionList = {
  [key: string]: () => void;
};

type KeySet = string[][];

type KeyCombo = {
  keys: string[];
  callback: (combo: KeyCombo, handler: InputHandler) => void;
};

type MouseEventAdapter = {
  mouseDownEvent?: (e: InputMouseEvent) => void;
  mouseUpEvent?: (e: InputMouseEvent) => void;
  mouseMoveEvent?: (e: InputMouseEvent) => void;
};

type KeyInputState = {
  [code: string]: boolean;
};

type MouseInputState = {
  [key: string]: boolean;
};

export type InputMouseEvent = {
  position: {
    x: number;
    y: number;
  };
  button: MouseButtonEnum;
  state: MouseInputState;
  anyPressed: () => boolean;
};

export enum MouseButtonEnum {
  Left = 'left',
  Middle = 'middle',
  Right = 'right',
}
